import React, { useState, useRef, useEffect } from 'react';
import { IconArrowsDownUp, IconFilter, IconTag, IconUser } from '@tabler/icons-react';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';

const SORT_OPTIONS = [
  { value: 'trending', label: 'Em alta' },
  { value: 'top', label: 'Top votos' },
  { value: 'new', label: 'Novo' },
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos status' },
  { value: 'open', label: 'Em aberto' },
  { value: 'under_review', label: 'Em análise' },
  { value: 'planned', label: 'Planejado' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'complete', label: 'Concluído' },
  { value: 'closed', label: 'Cancelado' },
];

function HubFilterPickerDropdown({ open, className = '', children }) {
  if (!open) return null;
  return (
    <div
      className={[
        'board-search-filters-popover',
        'board-search-filters-popover--simple',
        'hub-sugestoes-filter-dropdown',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}

export default function HubSugestoesFilters({
  sortBy = 'trending',
  onSortChange,
  statusFilter = '',
  onStatusFilterChange,
  listCategoryFilter = '',
  onListCategoryFilterChange,
  onlyMySuggestions = false,
  onOnlyMySuggestionsChange,
  categories = [],
  getStatusLabel,
  searchValue = '',
  onSearchChange,
  obscured = false,
}) {
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const [listCategoryDropdownOpen, setListCategoryDropdownOpen] = useState(false);
  const listCategoryDropdownRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) setSortDropdownOpen(false);
    };
    if (sortDropdownOpen) document.addEventListener('click', onOutside);
    return () => document.removeEventListener('click', onOutside);
  }, [sortDropdownOpen]);

  useEffect(() => {
    const onOutside = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) setStatusDropdownOpen(false);
    };
    if (statusDropdownOpen) document.addEventListener('click', onOutside);
    return () => document.removeEventListener('click', onOutside);
  }, [statusDropdownOpen]);

  useEffect(() => {
    const onOutside = (e) => {
      if (listCategoryDropdownRef.current && !listCategoryDropdownRef.current.contains(e.target)) {
        setListCategoryDropdownOpen(false);
      }
    };
    if (listCategoryDropdownOpen) document.addEventListener('click', onOutside);
    return () => document.removeEventListener('click', onOutside);
  }, [listCategoryDropdownOpen]);

  const currentSortOption = SORT_OPTIONS.find((o) => o.value === sortBy) || SORT_OPTIONS[0];
  const currentStatusLabel = statusFilter ? (getStatusLabel ? getStatusLabel(statusFilter) : statusFilter) : 'Todos status';
  const listCategoryOptions = [{ value: '', label: 'Todas as categorias' }, ...(categories || []).map((c) => ({ value: c.id, label: c.name || '' }))];
  const currentListCategoryLabel = listCategoryFilter
    ? (listCategoryOptions.find((o) => o.value === listCategoryFilter)?.label || listCategoryFilter)
    : 'Todas as categorias';

  return (
    <div
      className={`hub-feedback-toolbar-filters${obscured ? ' is-obscured' : ''}`}
      role="group"
      aria-label="Filtros de sugestões"
      aria-disabled={obscured || undefined}
    >
      <div ref={sortDropdownRef} className="hub-sugestoes-filter-anchor">
        <EcClickUpFilterChip
          label="Ordenar"
          value={currentSortOption.label}
          icon={IconArrowsDownUp}
          hasSelection={sortBy !== 'trending'}
          isOpen={sortDropdownOpen}
          ariaLabel="Ordenar sugestões"
          className="hub-sugestoes-filter-chip"
          onClick={() => {
            setStatusDropdownOpen(false);
            setListCategoryDropdownOpen(false);
            setSortDropdownOpen((v) => !v);
          }}
        />
        <HubFilterPickerDropdown open={sortDropdownOpen} className="hub-sugestoes-filter-dropdown--sort">
          <BoardSearchSimplePickerPanel
            showSearch={false}
            items={SORT_OPTIONS}
            getKey={(o) => o.value}
            getLabel={(o) => o.label}
            isSelected={(o) => sortBy === o.value}
            onToggle={(o) => {
              onSortChange?.(o.value);
              setSortDropdownOpen(false);
            }}
            listAriaLabel="Ordenar sugestões"
          />
        </HubFilterPickerDropdown>
      </div>

      <div ref={statusDropdownRef} className="hub-sugestoes-filter-anchor">
        <EcClickUpFilterChip
          label="Status"
          value={currentStatusLabel}
          icon={IconFilter}
          hasSelection={Boolean(statusFilter)}
          isOpen={statusDropdownOpen}
          ariaLabel="Filtrar por status"
          className="hub-sugestoes-filter-chip"
          onClick={() => {
            setSortDropdownOpen(false);
            setListCategoryDropdownOpen(false);
            setStatusDropdownOpen((v) => !v);
          }}
        />
        <HubFilterPickerDropdown open={statusDropdownOpen}>
          <BoardSearchSimplePickerPanel
            showSearch={false}
            items={STATUS_FILTER_OPTIONS}
            getKey={(o) => o.value || 'all'}
            getLabel={(o) => o.label}
            isSelected={(o) => statusFilter === o.value}
            onToggle={(o) => {
              onStatusFilterChange?.(o.value);
              setStatusDropdownOpen(false);
            }}
            listAriaLabel="Filtrar por status"
          />
        </HubFilterPickerDropdown>
      </div>

      <div ref={listCategoryDropdownRef} className="hub-sugestoes-filter-anchor">
        <EcClickUpFilterChip
          label="Categoria"
          value={currentListCategoryLabel}
          icon={IconTag}
          hasSelection={Boolean(listCategoryFilter)}
          isOpen={listCategoryDropdownOpen}
          ariaLabel="Filtrar por categoria"
          className="hub-sugestoes-filter-chip"
          onClick={() => {
            setSortDropdownOpen(false);
            setStatusDropdownOpen(false);
            setListCategoryDropdownOpen((v) => !v);
          }}
        />
        <HubFilterPickerDropdown open={listCategoryDropdownOpen} className="hub-sugestoes-filter-dropdown--category">
          <BoardSearchSimplePickerPanel
            showSearch={false}
            items={listCategoryOptions}
            getKey={(o) => o.value || 'all'}
            getLabel={(o) => o.label}
            isSelected={(o) => listCategoryFilter === o.value}
            onToggle={(o) => {
              onListCategoryFilterChange?.(o.value);
              setListCategoryDropdownOpen(false);
            }}
            listAriaLabel="Filtrar por categoria"
          />
        </HubFilterPickerDropdown>
      </div>

      <EcClickUpFilterChip
        label="Minhas sugestões"
        icon={IconUser}
        hasSelection={!!onlyMySuggestions}
        showValue={false}
        ariaLabel="Somente minhas sugestões"
        className="hub-sugestoes-filter-chip"
        tooltip="Ao marcar, visualiza somente meus feedbacks"
        tooltipMultiline
        onClick={() => onOnlyMySuggestionsChange?.(!onlyMySuggestions)}
      />

      <span className="board-toolbar-separator" aria-hidden="true">
        |
      </span>

      <BoardToolbarExpandSearch
        value={searchValue}
        onChange={onSearchChange}
        placeholder="Pesquisar..."
        ariaLabel="Pesquisar sugestões"
      />
    </div>
  );
}
